import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { toIsoDateString, toDate } from './shiftDate';

// Given a tenantId and a time entry-like object { employeeId, timestamp, date, locationId, roleId }
// attempt to find the best matching scheduled shift document for that employee on the same day
// or a previous overnight shift. Returns the shift doc object { id, ...data } or null.
export default async function findMatchingShift(tenantId, entry) {
  if (!tenantId || !entry || !entry.employeeId || !entry.timestamp) return null;

  // Normalize timestamp -> Date
  const tsDate = entry.timestamp && entry.timestamp.toDate ? entry.timestamp.toDate() : new Date(entry.timestamp);
  const dayKey = entry?.date && /^\d{4}-\d{2}-\d{2}$/.test(String(entry.date))
    ? entry.date
    : toIsoDateString(tsDate);

  const dayStart = new Date(`${dayKey}T00:00:00Z`);
  const nextDayStart = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);

  try {
    // Query shifts for that employee on that day
    const q = query(
      collection(db, `tenants/${tenantId}/shifts`),
      where('employeeId', '==', entry.employeeId),
      where('shiftDateKey', '>=', dayKey),
      where('shiftDateKey', '<', toIsoDateString(nextDayStart))
    );

    const snap = await getDocs(q);
    const candidates = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => !s.deleted);

    // If none found for the same day, also check previous day's shifts (overnight)
    if (candidates.length === 0) {
      const prevDayStart = new Date(dayStart.getTime() - 24 * 60 * 60 * 1000);
      const prevDayKey = toIsoDateString(prevDayStart);
      const q2 = query(
        collection(db, `tenants/${tenantId}/shifts`),
        where('employeeId', '==', entry.employeeId),
        where('shiftDateKey', '>=', prevDayKey),
        where('shiftDateKey', '<', dayKey)
      );
      const snap2 = await getDocs(q2);
      candidates.push(...snap2.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => !s.deleted));
    }

    // Extra fallback: if still no candidates, try a broader query for the employee and filter client-side by date keys.
    // This helps when shiftDate stored types or timezone edge-cases cause the range queries to miss matches.
    if (candidates.length === 0) {
      try {
        const wideQ = query(collection(db, `tenants/${tenantId}/shifts`), where('employeeId', '==', entry.employeeId));
        const wideSnap = await getDocs(wideQ);
        const wideCandidates = wideSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => !s.deleted);

        const prevDayKey = toIsoDateString(new Date(dayStart.getTime() - 24 * 60 * 60 * 1000));
        // Keep candidates whose shiftDate matches dayKey or previous day
        for (const s of wideCandidates) {
          const sDateKey = s.shiftDateKey || toIsoDateString(s.shiftDate);
          if (sDateKey === dayKey || sDateKey === prevDayKey) candidates.push(s);
        }
      } catch (err) {
        // non-fatal - continue
      }
    }

    if (!candidates.length) return null;

    // Score candidates by match quality
    const entryTime = tsDate.getTime();
    let best = null;
    let bestScore = -Infinity;
    let bestWithinWindow = false;
    const maxOutsideWindowMs = 4 * 60 * 60 * 1000; // never auto-link if >4h outside scheduled window

    const parseShiftDateTime = (shift, timeStr) => {
      if (!timeStr) return null;
      const shiftDate = shift.shiftDateKey ? toDate(shift.shiftDateKey) : toDate(shift.shiftDate);
      const [hh, mm] = timeStr.split(':').map(Number);
      if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
      const d = new Date(shiftDate);
      d.setHours(hh, mm, 0, 0);
      return d;
    };

    for (const s of candidates) {
      let score = 0;
      let candidateWithinWindow = false;

      // location match
      if (entry.locationId && s.locationId && entry.locationId === s.locationId) score += 2;

      // role match
      if (entry.roleId && s.roleId && entry.roleId === s.roleId) score += 1;

      // If shift has start/end times, check if entry timestamp falls within an expanded window
      const start = parseShiftDateTime(s, s.shiftStartTime || s.startTime || s.shiftStart || s.start);
      const end = parseShiftDateTime(s, s.shiftEndTime || s.endTime || s.shiftEnd || s.end);

      if (start && end) {
        // If end is before or equal to start assume overnight and add 24h to end
        if (end.getTime() <= start.getTime()) {
          end.setDate(end.getDate() + 1);
        }

        const expandedStart = start.getTime() - 60 * 60 * 1000;
        const expandedEnd = end.getTime() + 60 * 60 * 1000;
        const inExpandedWindow = entryTime >= expandedStart && entryTime <= expandedEnd;

        // Guardrail: avoid linking a next-day/previous-day punch to an unrelated shift.
        const outsideDistanceMs = entryTime < expandedStart
          ? expandedStart - entryTime
          : entryTime > expandedEnd
            ? entryTime - expandedEnd
            : 0;
        if (!inExpandedWindow && outsideDistanceMs > maxOutsideWindowMs) {
          continue;
        }

        // If entry within shift window -> strong match
        if (inExpandedWindow) {
          score += 10;
          candidateWithinWindow = true;
        } else {
          // penalize distance from shift window center
          const center = (start.getTime() + end.getTime()) / 2;
          const distHours = Math.abs(entryTime - center) / (1000 * 60 * 60);
          score += Math.max(0, 5 - distHours); // closer gets higher
          candidateWithinWindow = true;
        }
      } else {
        // fallback: prefer shift whose shiftDate equals entry date
        const shiftDateKey = s.shiftDateKey || toIsoDateString(s.shiftDate);
        if (shiftDateKey === dayKey) {
          score += 3;
          candidateWithinWindow = true;
        }
      }

      // prefer published shifts slightly
      if (s.isPublished) score += 0.5;

      if (score > bestScore) {
        bestScore = score;
        best = s;
        bestWithinWindow = candidateWithinWindow;
      }
    }

    // Only return if we have a meaningful, in-window candidate.
    return best && bestScore > -1 && bestWithinWindow ? best : null;
  } catch (err) {
    console.error('findMatchingShift error', err);
    return null;
  }
}
